import axios, { AxiosResponse } from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { AggregateOn } from 'components/enums';

import {
  CAPACITY_PLAN_REPORT,
  SCENARIO_OVERVIEW_BASEPLAN,
  SCENARIO_OVERVIEW_FORMATION,
  SCENARIO_OVERVIEW_ROSTER,
  SCENARIO_OVERVIEW_WORK_DEMAND
} from 'RestAPIs/endpoints';

import { showSnackbar } from 'components/shared';

type Period = {
  hours: number;
  periodNr: number;
};

export type WorkDemandOverview = {
  category: string;
  periods: Period[];
  id: string;
};

export type BaseplanOverview = {
  category: string;
  functionDescription: string;
  functionId: number;
  functionColor: string;
  periods: Period[];
  isBasePlan: boolean;
  id: string;
};

export type FormationOverview = {
  functionColor: string;
  functionDescription: string;
  functionId: number;
  periods: Period[];
  id: string;
};

export type RosterOverview = {
  category?: string;
  functionDescription: string;
  functionId: number;
  periods: Period[];
  id: string;
};

export type AggregateOnType = keyof typeof AggregateOn;

type Options = {
  scenarioId: number;
  year: number;
  aggregateOn: AggregateOnType;
  signal?: AbortSignal;
};

const createAxiosInstance = () => {
  const instance = axios.create();

  instance.interceptors.response.use(
    (
      response: AxiosResponse<
        (
          | WorkDemandOverview
          | BaseplanOverview
          | FormationOverview
          | RosterOverview
        )[]
      >
    ) => {
      response.data.forEach((entry) => {
        entry.id = uuidv4();
      });
      return response;
    }
  );
  instance.interceptors.request = axios.interceptors.request;

  return instance;
};

export const getScenarioWorkDemandOverview = ({
  scenarioId,
  aggregateOn,
  year,
  signal
}: Options) =>
  createAxiosInstance().get<WorkDemandOverview[]>(
    SCENARIO_OVERVIEW_WORK_DEMAND,
    {
      signal,
      params: { year, aggregateOn },
      pathParams: { scenarioId }
    }
  );

export const getScenarioBaseplanOverview = ({
  scenarioId,
  aggregateOn,
  year,
  signal
}: Options) =>
  createAxiosInstance().get<BaseplanOverview[]>(SCENARIO_OVERVIEW_BASEPLAN, {
    signal,
    params: { year, aggregateOn },
    pathParams: { scenarioId }
  });

export const getScenarioFormationOverview = ({
  scenarioId,
  aggregateOn,
  year,
  signal
}: Options) =>
  createAxiosInstance().get<FormationOverview[]>(SCENARIO_OVERVIEW_FORMATION, {
    signal,
    params: { year, aggregateOn },
    pathParams: { scenarioId }
  });

export const getScenarioRosterOverview = ({
  scenarioId,
  aggregateOn,
  year,
  signal
}: Options) =>
  createAxiosInstance().get<RosterOverview[]>(SCENARIO_OVERVIEW_ROSTER, {
    signal,
    params: { year, aggregateOn },
    pathParams: { scenarioId }
  });

export const getAnalyzerData = ({
  scenarioId,
  aggregateOn,
  year,
  signal
}: Options) =>
  Promise.all([
    getScenarioWorkDemandOverview({ scenarioId, aggregateOn, year, signal }),
    getScenarioBaseplanOverview({ scenarioId, aggregateOn, year, signal }),
    getScenarioFormationOverview({ scenarioId, aggregateOn, year, signal }),
    getScenarioRosterOverview({ scenarioId, aggregateOn, year, signal })
  ]).then(
    ([
      { data: WORK_DEMAND },
      { data: BASE_PLAN },
      { data: FORMATION },
      { data: ROSTER }
    ]) => ({
      WORK_DEMAND,
      BASE_PLAN,
      FORMATION,
      ROSTER
    })
  );

export const downloadReport = ({ scenarioId, year, aggregateOn }: Options) =>
  axios
    .get(CAPACITY_PLAN_REPORT, {
      params: { year, aggregateOn },
      pathParams: { scenarioId }
    })
    .then(() => {
      showSnackbar({
        messageCode: 'generatedReportStarted',
        messageCodeProps: { reportType: 'generatedReport.capplan' },
        severity: 'success',
        icon: 'info',
        autoHideDuration: 6000
      });
    });
